import type {
  PurchaseCostObject,
  PurchaseCostObjectType,
  PurchaseJobPlBucket,
  PurchaseOverheadGroup,
  PurchaseRequisition,
  PurchaseSpendClass,
} from '../types/purchasing';

/** Easy-to-edit overhead group lookup (id + label). */
export const PURCHASE_OVERHEAD_GROUPS: ReadonlyArray<{
  id: PurchaseOverheadGroup;
  label: string;
}> = [
  { id: 'shop_tools_small_equipment', label: 'Shop tools / small equipment' },
  { id: 'facility_office_improvements', label: 'Facility / office improvements' },
  { id: 'safety_consumables', label: 'Safety consumables' },
  { id: 'software_admin', label: 'Office Expenses' },
  { id: 'training_crew_enablement', label: 'Training / crew enablement' },
  { id: 'crew_investment', label: 'Crew Investment' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'vehicles', label: 'Vehicles' },
];

export const PURCHASE_OVERHEAD_GROUP_IDS: readonly PurchaseOverheadGroup[] =
  PURCHASE_OVERHEAD_GROUPS.map(g => g.id);

export const PURCHASE_SPEND_CLASS_LABELS: Record<PurchaseSpendClass, string> = {
  job: 'Job',
  overhead: 'Overhead',
  capex: 'CapEx',
};

export const PURCHASE_JOB_PL_BUCKETS: ReadonlyArray<{
  id: PurchaseJobPlBucket;
  label: string;
}> = [
  { id: 'timber', label: 'Other materials' },
  { id: 'install', label: 'Install' },
  { id: 'travel', label: 'Travel' },
  { id: 'design', label: 'Design' },
  { id: 'engineering', label: 'Engineering' },
  { id: 'production', label: 'Production' },
  { id: 'freight', label: 'Freight' },
];

export const PURCHASE_JOB_PL_BUCKET_IDS: readonly PurchaseJobPlBucket[] =
  PURCHASE_JOB_PL_BUCKETS.map(b => b.id);

export const PURCHASE_JOB_PL_BUCKET_LABELS: Record<PurchaseJobPlBucket, string> =
  Object.fromEntries(PURCHASE_JOB_PL_BUCKETS.map(b => [b.id, b.label])) as Record<
    PurchaseJobPlBucket,
    string
  >;

/**
 * CapEx classification threshold (USD). Placeholder for Matthew/CFO —
 * leave null until a production value is set. Not business gospel.
 * TODO: replace with approved CapEx threshold when finance confirms.
 */
export const CAPEX_THRESHOLD_USD: number | null = null;

/**
 * Discretionary OpEx envelope for overhead improvements/tools.
 * Placeholders only — do not treat unset values as an approved budget.
 * TODO: set monthly (and optional quarterly) dollars when Matthew/CFO confirm.
 */
export interface OverheadOpexEnvelopeConfig {
  /** Monthly discretionary OpEx envelope in USD, or null if unset. */
  monthlyUsd: number | null;
  /** Optional quarterly envelope in USD, or null if unset. */
  quarterlyUsd: number | null;
}

export const OVERHEAD_OPEX_ENVELOPE: OverheadOpexEnvelopeConfig = {
  monthlyUsd: null,
  quarterlyUsd: null,
};

const COMMITTED_STATUSES = new Set([
  'pending_approval',
  'in_review',
  'approved',
  'purchased',
]);

export function isPurchaseOverheadGroup(value: unknown): value is PurchaseOverheadGroup {
  return typeof value === 'string' && (PURCHASE_OVERHEAD_GROUP_IDS as readonly string[]).includes(value);
}

export function isPurchaseSpendClass(value: unknown): value is PurchaseSpendClass {
  return value === 'job' || value === 'overhead' || value === 'capex';
}

export function isPurchaseJobPlBucket(value: unknown): value is PurchaseJobPlBucket {
  return typeof value === 'string' && (PURCHASE_JOB_PL_BUCKET_IDS as readonly string[]).includes(value);
}

export function jobPlBucketLabel(bucket: PurchaseJobPlBucket | undefined): string | undefined {
  if (!bucket) return undefined;
  return PURCHASE_JOB_PL_BUCKET_LABELS[bucket] || bucket;
}

export function overheadGroupLabel(group: PurchaseOverheadGroup | undefined): string | undefined {
  if (!group) return undefined;
  return PURCHASE_OVERHEAD_GROUPS.find(g => g.id === group)?.label || group;
}

/** Cost-object type for P&L tagging. Approval routing uses spend class. */
export function costTypeForSpendClass(spendClass: PurchaseSpendClass): PurchaseCostObjectType {
  return spendClass === 'job' ? 'project' : 'overhead';
}

/**
 * Infer spend class for legacy rows that only have costObject.type.
 * Unclassified overhead is treated as overhead (not CapEx).
 */
export function resolveSpendClass(costObject: PurchaseCostObject): PurchaseSpendClass {
  if (isPurchaseSpendClass(costObject.spendClass)) return costObject.spendClass;
  return costObject.type === 'project' ? 'job' : 'overhead';
}

export function formatCostObjectLabel(costObject: PurchaseCostObject): string {
  const spend = resolveSpendClass(costObject);
  if (spend === 'job') {
    const node = jobPlBucketLabel(costObject.plBucket);
    const job = costObject.label || 'Job';
    return node ? `${job} · ${node}` : job;
  }
  if (spend === 'capex') {
    return costObject.label || 'CapEx';
  }
  const group = overheadGroupLabel(costObject.overheadGroup);
  if (group) return `Overhead · ${group}`;
  return costObject.label || 'Overhead';
}

export function validatePurchaseCostObject(costObject: PurchaseCostObject): string | null {
  const spend = resolveSpendClass(costObject);
  if (spend !== 'overhead' && costObject.overheadExpenseId?.trim()) {
    return 'Only overhead spend can pay a forecast line.';
  }
  if (spend === 'job') {
    if (!costObject.dealId?.trim() && !costObject.projectId?.trim()) {
      return 'Job spend must be tagged to a job / project.';
    }
    if (!isPurchaseJobPlBucket(costObject.plBucket)) {
      return 'Job spend must pick a proposal node (Materials, Install, Travel, …).';
    }
    if (costObject.overheadGroup) {
      return 'Job spend cannot include an overhead group.';
    }
    return null;
  }
  if (spend === 'overhead') {
    if (!isPurchaseOverheadGroup(costObject.overheadGroup)) {
      return 'Overhead spend must pick an overhead group.';
    }
    if (costObject.dealId || costObject.projectId) {
      return 'Overhead spend cannot be tagged to a job.';
    }
    if (costObject.plBucket) {
      return 'Overhead spend cannot include a proposal node.';
    }
    return null;
  }
  // capex — company capital; no job, no overhead group required
  if (costObject.dealId || costObject.projectId) {
    return 'CapEx spend cannot be tagged to a job.';
  }
  if (costObject.overheadGroup) {
    return 'CapEx spend cannot include an overhead group.';
  }
  if (costObject.plBucket) {
    return 'CapEx spend cannot include a proposal node.';
  }
  return null;
}

export function buildPurchaseCostObject(input: {
  spendClass: PurchaseSpendClass;
  overheadGroup?: PurchaseOverheadGroup | '';
  /** overheadExpenses id. Omit to count the PO in the group budget only. */
  overheadExpenseId?: string;
  plBucket?: PurchaseJobPlBucket | '';
  dealId?: string;
  projectId?: string;
  jobLabel?: string;
}): PurchaseCostObject {
  const spendClass = input.spendClass;
  const type = costTypeForSpendClass(spendClass);
  if (spendClass === 'job') {
    const plBucket = isPurchaseJobPlBucket(input.plBucket) ? input.plBucket : undefined;
    return {
      type,
      spendClass,
      ...(input.dealId ? { dealId: input.dealId } : {}),
      ...(input.projectId ? { projectId: input.projectId } : {}),
      ...(plBucket ? { plBucket } : {}),
      label: input.jobLabel || 'Job',
    };
  }
  if (spendClass === 'overhead') {
    const overheadGroup = isPurchaseOverheadGroup(input.overheadGroup)
      ? input.overheadGroup
      : undefined;
    const overheadExpenseId = input.overheadExpenseId?.trim() || '';
    return {
      type,
      spendClass,
      ...(overheadGroup ? { overheadGroup } : {}),
      ...(overheadExpenseId ? { overheadExpenseId } : {}),
      label: overheadGroup ? overheadGroupLabel(overheadGroup) : 'Overhead',
    };
  }
  return {
    type,
    spendClass: 'capex',
    label: 'CapEx',
  };
}

function chicagoYearMonth(date: Date): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(date);
  const year = Number(parts.find(p => p.type === 'year')?.value);
  const month = Number(parts.find(p => p.type === 'month')?.value);
  return {
    year: Number.isFinite(year) ? year : date.getFullYear(),
    month: Number.isFinite(month) ? month : date.getMonth() + 1,
  };
}

function chicagoQuarter(month: number): number {
  return Math.ceil(month / 3);
}

export function isOverheadEnvelopeCandidate(row: Pick<PurchaseRequisition, 'costObject' | 'status'>): boolean {
  if (!COMMITTED_STATUSES.has(row.status)) return false;
  return resolveSpendClass(row.costObject) === 'overhead';
}

export interface OverheadEnvelopeSummary {
  config: OverheadOpexEnvelopeConfig;
  periodLabel: string;
  committedUsd: number;
  monthlyRemainingUsd: number | null;
  quarterlyCommittedUsd: number;
  quarterlyRemainingUsd: number | null;
  /** True when both envelope amounts are still placeholders. */
  envelopeUnset: boolean;
}

/**
 * Lightweight remaining-envelope summary for overhead OpEx POs.
 * Uses createdAt as the commit date for period bucketing.
 */
export function summarizeOverheadEnvelope(
  rows: PurchaseRequisition[],
  now: Date = new Date(),
  config: OverheadOpexEnvelopeConfig = OVERHEAD_OPEX_ENVELOPE,
): OverheadEnvelopeSummary {
  const { year, month } = chicagoYearMonth(now);
  const quarter = chicagoQuarter(month);
  let committedUsd = 0;
  let quarterlyCommittedUsd = 0;

  for (const row of rows) {
    if (!isOverheadEnvelopeCandidate(row)) continue;
    const created = chicagoYearMonth(row.createdAt);
    if (created.year === year && chicagoQuarter(created.month) === quarter) {
      quarterlyCommittedUsd += row.amount || 0;
    }
    if (created.year === year && created.month === month) {
      committedUsd += row.amount || 0;
    }
  }

  const monthlyRemainingUsd =
    config.monthlyUsd == null ? null : Math.max(0, config.monthlyUsd - committedUsd);
  const quarterlyRemainingUsd =
    config.quarterlyUsd == null ? null : Math.max(0, config.quarterlyUsd - quarterlyCommittedUsd);

  return {
    config,
    periodLabel: `${year}-${String(month).padStart(2, '0')}`,
    committedUsd,
    monthlyRemainingUsd,
    quarterlyCommittedUsd,
    quarterlyRemainingUsd,
    envelopeUnset: config.monthlyUsd == null && config.quarterlyUsd == null,
  };
}
